import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../ui/modal';
import { useActiveFamily } from '../../context/ActiveFamilyContext';
import { useFamilyStaticData } from '../../hooks/useFamilyStaticData';

interface MemberExpiredPlanModalProps {
  isOpen: boolean;
  ownerName?: string;
}

/**
 * Modal que se muestra a miembros NO-OWNER cuando el plan del admin está vencido
 * Les indica que deben esperar a que el admin renueve o contactarlo
 */
export default function MemberExpiredPlanModal({ isOpen, ownerName }: MemberExpiredPlanModalProps) {
  const { t } = useTranslation();
  const { activeFamily } = useActiveFamily();
  const familyStatic = useFamilyStaticData(activeFamily?.id || null);
  
  // Buscar información del owner
  const owner = familyStatic.members.find((m: any) => m.id === activeFamily?.ownerId);
  const displayOwnerName = ownerName || owner?.name || t('subscription.theAdmin');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // No permitir cerrar
      showCloseButton={false}
      className="max-w-md"
    >
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="text-6xl mb-4">⏸️</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('subscription.expiredPlan')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('subscription.groupPlanExpired')}
          </p>
        </div>

        {/* Info del owner */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6 border-2 border-amber-200 dark:border-amber-800">
          <div className="flex gap-4">
            <div className="text-3xl flex-shrink-0">👤</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('subscription.contactAdmin')}
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                <strong>{displayOwnerName}</strong> {t('subscription.ownerMustRenew')}
              </p>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  <span>{t('subscription.askToRenew')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 font-bold mt-0.5">•</span>
                  <span>{t('subscription.onceRenewed')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Lo que pueden hacer */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border-2 border-blue-200 dark:border-blue-800">
          <div className="flex gap-4">
            <div className="text-3xl flex-shrink-0">💡</div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {t('subscription.meanwhile')}
              </h3>
              <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">✓</span>
                  <span>{t('subscription.progressSaved')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">✓</span>
                  <span>{t('subscription.readOnlyAccess')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold mt-0.5">✓</span>
                  <span>{t('subscription.onceRenewedNormal')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Información adicional */}
        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('subscription.messageDisappears', { name: displayOwnerName })}
          </p>
        </div>
      </div>
    </Modal>
  );
}
